// Duplicated from api/src/accounts/tax-registration.ts — CRA's build
// rejects value-imports from outside client/src/. Keep in sync by hand
// until a shared package is introduced.

export type TaxRegistration =
  | "taxable"
  | "tax_deferred"
  | "tax_free"
  | "inherited_deferred"
  | "inherited_free";

export function isRothAccountType(type: string | null | undefined): boolean {
  if (!type) return false;
  return /\broth\b/i.test(type);
}

export function isRmdEligibleAccountType(
  type: string | null | undefined,
): boolean {
  if (!type) return false;
  if (isRothAccountType(type)) return false;
  const normalized = type.toLowerCase();
  return (
    /\bira\b/.test(normalized) ||
    /\b401\s*\(?k\)?\b/.test(normalized) ||
    /\b403\s*\(?b\)?\b/.test(normalized) ||
    /\b457\s*\(?b\)?\b/.test(normalized) ||
    /\bprofit\s+sharing\b/.test(normalized)
  );
}

export function isHsaAccountType(type: string | null | undefined): boolean {
  if (!type) return false;
  return /\bhsa\b|\bhealth\s+savings\b/i.test(type);
}

export function isEducationSavingsAccountType(
  type: string | null | undefined,
): boolean {
  if (!type) return false;
  return /\beducation\s+savings\b|\bcoverdell\b|\b529\b/i.test(type);
}

export function isDonorAdvisedAccountType(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  return /\bdonor[\s-]?advised\b/i.test(value);
}

export function isTaxableAccountType(type: string | null | undefined): boolean {
  if (!type) return false;
  return /\b(individual|joint|jtwros|tenants?|trust|estate|ugma|utma|llc|corp(?:oration)?|partnership|designated|brokerage|taxable|community\s+property)\b/i.test(
    type,
  );
}

export function classifyTaxRegistrationFromType(
  type: string | null | undefined,
): TaxRegistration | null {
  if (!type) return null;
  const inherited = /\binherited\b/i.test(type);
  if (isRothAccountType(type)) {
    return inherited ? "inherited_free" : "tax_free";
  }
  // A donor-advised fund is sometimes named/structured as a trust, so it is
  // matched before the taxable-entity check below to avoid a
  // "… Donor Advised Trust" resolving to taxable.
  if (isDonorAdvisedAccountType(type)) {
    return "tax_free";
  }
  if (isRmdEligibleAccountType(type)) {
    return inherited ? "inherited_deferred" : "tax_deferred";
  }
  // Checked before HSA / education-savings: an entity keyword (LLC, brokerage,
  // trust, …) wins over a stray "529"/"HSA" token elsewhere in the string, so
  // e.g. "529 Broadway LLC" is taxable rather than a tax-free 529.
  if (isTaxableAccountType(type)) {
    return "taxable";
  }
  if (isHsaAccountType(type) || isEducationSavingsAccountType(type)) {
    return "tax_free";
  }
  return null;
}

/**
 * Registration keywords that double as common personal names, making them
 * unsafe to match in an account name we could NOT split on the owner: "Roth"
 * is a surname and "Ira" a first name. Inside a stripped suffix (owner removed)
 * these are fine — it is only a full, unsplit name that is ambiguous.
 */
const NAME_COLLIDING_KEYWORD = /\broth\b|\bira\b/i;

/**
 * Infer a TaxRegistration from the account NAME when the custodian `type`
 * string is blank/unmapped. Some feeds (notably Fidelity/NFS via Bridge) leave
 * `type` empty but encode the registration in the account name, e.g.
 * "JANE DOE Roth IRA".
 *
 * The registration to classify is the part of the name that FOLLOWS the owner,
 * so a client whose surname is "Roth" (or first name "Ira") is not
 * misclassified from the personal-name portion. When the owner can be stripped
 * off the front (on a word boundary) we classify only the trailing suffix.
 * When the owner cannot be isolated (missing/blank, not a prefix, or equal to
 * the whole name), an unsplit name containing a personal-name-colliding keyword
 * ("roth"/"ira") is left Unknown rather than risk mislabelling a taxable
 * account; names with only unambiguous keywords (401k, trust, …) still
 * classify.
 */
export function classifyTaxRegistrationFromName(
  name: string | null | undefined,
  owner?: string | null,
): TaxRegistration | null {
  // A DAF sponsor name ("… Donor Advised …") may live in the owner field even
  // when the account name is blank or "Unknown"; the phrase is unambiguous, so
  // match it directly. (Name-embedded DAFs are caught by the suffix path below
  // via classifyTaxRegistrationFromType.)
  if (isDonorAdvisedAccountType(owner)) return "tax_free";
  if (!name) return null;

  const trimmedName = name.trim();
  const trimmedOwner = owner?.trim();

  // If the name begins with the owner on a word boundary, the registration is
  // the trailing remainder — classify only that. Requiring the boundary stops
  // a short/partial owner ("Lee") from slicing into the next token
  // ("Leeland …") and leaving keyword-bearing residue.
  if (
    trimmedOwner &&
    trimmedName.toLowerCase().startsWith(trimmedOwner.toLowerCase()) &&
    (trimmedName.length === trimmedOwner.length ||
      /\s/.test(trimmedName.charAt(trimmedOwner.length)))
  ) {
    const suffix = trimmedName.slice(trimmedOwner.length).trim();
    // A non-empty suffix is the owner-stripped registration and is safe to
    // classify in full. An empty suffix means owner === name; fall through to
    // the unsplit-name handling below.
    if (suffix) return classifyTaxRegistrationFromType(suffix);
  }

  // Owner could not be isolated (missing, not a prefix, or owner === name).
  // Only classify when the name carries no personal-name-colliding keyword.
  if (NAME_COLLIDING_KEYWORD.test(trimmedName)) return null;
  return classifyTaxRegistrationFromType(trimmedName);
}

const TAX_REGISTRATION_DETAILS: Record<
  TaxRegistration,
  {
    isTaxable: boolean;
    isTaxDeferred: boolean;
    isTaxFree: boolean;
    isInherited: boolean;
    label: string;
  }
> = {
  taxable: {
    isTaxable: true,
    isTaxDeferred: false,
    isTaxFree: false,
    isInherited: false,
    label: "Taxable",
  },
  tax_deferred: {
    isTaxable: false,
    isTaxDeferred: true,
    isTaxFree: false,
    isInherited: false,
    label: "Tax-Deferred",
  },
  tax_free: {
    isTaxable: false,
    isTaxDeferred: false,
    isTaxFree: true,
    isInherited: false,
    label: "Tax-Free (Roth)",
  },
  inherited_deferred: {
    isTaxable: false,
    isTaxDeferred: true,
    isTaxFree: false,
    isInherited: true,
    label: "Inherited IRA",
  },
  inherited_free: {
    isTaxable: false,
    isTaxDeferred: false,
    isTaxFree: true,
    isInherited: true,
    label: "Inherited Roth IRA",
  },
};

export function resolveAccountTaxType(account: {
  taxRegistration?: TaxRegistration | null;
  isTaxable?: boolean;
  isTaxDeferred?: boolean;
  type?: string | null;
  displayName?: string | null;
  owner?: string | null;
}): {
  isTaxable: boolean;
  isTaxDeferred: boolean;
  isTaxFree: boolean;
  isInherited: boolean;
  isOverridden: boolean;
  label: string;
} {
  // An explicit advisor override always wins; otherwise infer from the
  // custodian's account-type string, and finally from the account name when
  // the type is blank/unmapped.
  const registration =
    account.taxRegistration ??
    classifyTaxRegistrationFromType(account.type) ??
    classifyTaxRegistrationFromName(account.displayName, account.owner);
  if (registration) {
    return {
      ...TAX_REGISTRATION_DETAILS[registration],
      isOverridden: account.taxRegistration != null,
    };
  }

  // Type and name both unmapped. Fall back to the legacy Bridge boolean flags,
  // which themselves may be unset → "Unknown".
  return {
    isTaxable: account.isTaxable ?? false,
    isTaxDeferred: account.isTaxDeferred ?? false,
    isTaxFree: false,
    isInherited: false,
    isOverridden: false,
    label: account.isTaxable
      ? "Taxable"
      : account.isTaxDeferred
        ? "Tax-Deferred"
        : "Unknown",
  };
}
